/*
 * Context 用量指示器 — UI 对齐 XHeyClaw-Web context-usage-indicator.tsx (Tailwind+shadcn 版)
 *
 * 形态:
 *   - trigger: 26px 圆形按钮 + SVG 环形进度条 (颜色 + 弧长, 无文字)
 *   - popover: hero header (大百分比 + gradient bar + tokens 拆分排版 + effective input 卡片)
 *             + 占用明细带剩余空间
 *             + Cache 三栏 / 工具调用 / 消息 / 多模态 / Tool schema 等可折叠区
 *
 * 字段来自 contextUsage.ts (1:1 移植 XHeyClaw-Web schema):
 *   parts / debugParts / systemPromptSections / cacheBreakdown / toolInvocations /
 *   messageEntries / multimodal / toolSchemaEntries / effectiveLimit / maxOutputTokens / warnings
 */
import React, { useState } from 'react'
import { Popover } from 'antd'
import {
  formatPercent,
  formatTokens,
  getContextUsageTone,
  estimateUsedTokens,
  PART_COLORS
} from './contextUsage'
import type {
  SessionContextUsage,
  ContextUsagePart,
  ContextUsageEntry,
  CacheBreakdown,
  ToolInvocationEntry,
  MessageEntry,
  MultimodalBreakdown,
  ToolSchemaEntry
} from './contextUsage'
import './ContextUsageIndicator.scss'

interface Props {
  usage: SessionContextUsage | null
}

type Tone = ReturnType<typeof getContextUsageTone>

const TONE_CLASS: Record<Tone, string> = {
  ok: 'is-ok',
  warn: 'is-warn',
  danger: 'is-danger',
  unknown: 'is-unknown'
}

/** "44.1K" → ["44.1", "K"] — 让单位用小一号字号 */
function splitNumberUnit(s: string): [string, string] {
  const match = s.match(/^([\d.]+)([A-Za-z]*)$/)
  return match ? [match[1], match[2]] : [s, '']
}

/* ── Collapsible 折叠区 ────────────────────────────────── */

const CollapsibleSection: React.FC<{
  title: string
  count?: number
  defaultOpen?: boolean
  children: React.ReactNode
}> = ({ title, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div
      className={`aiClerk-ctxind__collapse${open ? ' is-open' : ' is-closed'}`}
    >
      <button
        type="button"
        className="aiClerk-ctxind__collapse-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="aiClerk-ctxind__collapse-dot" aria-hidden />
        <span className="aiClerk-ctxind__collapse-caret" aria-hidden>
          ▸
        </span>
        <span className="aiClerk-ctxind__collapse-title">{title}</span>
        {count !== undefined && (
          <span className="aiClerk-ctxind__collapse-count">{count}</span>
        )}
      </button>
      {open && (
        <div className="aiClerk-ctxind__collapse-content">{children}</div>
      )}
    </div>
  )
}

/* ── PartRow ─────────────────────────────────────────── */

const PartRow: React.FC<{ part: ContextUsagePart; filled?: boolean }> = ({
  part,
  filled = true
}) => {
  const dotClass = PART_COLORS[part.id] || 'aiClerk-ctxind__dot--default'
  return (
    <div className="aiClerk-ctxind__part-row">
      <span
        className={`aiClerk-ctxind__part-dot ${dotClass}${
          filled ? '' : ' is-hollow'
        }`}
        aria-hidden
      />
      <span className="aiClerk-ctxind__part-label">{part.label}</span>
      <span className="aiClerk-ctxind__part-tokens">
        {formatTokens(part.tokens)}
      </span>
      {part.percentOfContext !== undefined && (
        <span className="aiClerk-ctxind__part-pct">
          {formatPercent(part.percentOfContext)}
        </span>
      )}
    </div>
  )
}

/* ── Entry list row ──────────────────────────────────── */

const EntryRow: React.FC<{ entry: ContextUsageEntry }> = ({ entry }) => (
  <div className="aiClerk-ctxind__entry-row">
    <span className="aiClerk-ctxind__entry-name">
      {entry.name}
      {entry.truncated && (
        <span className="aiClerk-ctxind__entry-truncated">截断</span>
      )}
    </span>
    <span className="aiClerk-ctxind__entry-tokens">
      {formatTokens(entry.tokens)}
    </span>
  </div>
)

/* ── Cache 三栏 ─────────────────────────────────────── */

const CacheBreakdownPanel: React.FC<{ cache: CacheBreakdown }> = ({
  cache
}) => {
  const total = cache.inputUncached + cache.cacheRead + cache.cacheWrite
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0)
  return (
    <div className="aiClerk-ctxind__cache">
      <div className="aiClerk-ctxind__cache-title">Cache 分栏</div>
      <div className="aiClerk-ctxind__cache-row">
        <span className="aiClerk-ctxind__cache-dot is-input" />
        <span className="aiClerk-ctxind__cache-label">未缓存 input</span>
        <span className="aiClerk-ctxind__cache-tokens">
          {formatTokens(cache.inputUncached)}
        </span>
        <span className="aiClerk-ctxind__cache-pct">
          ({pct(cache.inputUncached)}%)
        </span>
      </div>
      <div className="aiClerk-ctxind__cache-row">
        <span className="aiClerk-ctxind__cache-dot is-hit" />
        <span
          className="aiClerk-ctxind__cache-label"
          title="Cache 命中: 按 ~0.1x 价格计费, 省钱"
        >
          Cache 命中
        </span>
        <span className="aiClerk-ctxind__cache-tokens">
          {formatTokens(cache.cacheRead)}
        </span>
        <span className="aiClerk-ctxind__cache-pct">
          ({pct(cache.cacheRead)}%)
        </span>
      </div>
      <div className="aiClerk-ctxind__cache-row">
        <span className="aiClerk-ctxind__cache-dot is-write" />
        <span
          className="aiClerk-ctxind__cache-label"
          title="Cache 写入: 按 ~1.25x 价格计费, 一次性"
        >
          Cache 写入
        </span>
        <span className="aiClerk-ctxind__cache-tokens">
          {formatTokens(cache.cacheWrite)}
        </span>
        <span className="aiClerk-ctxind__cache-pct">
          ({pct(cache.cacheWrite)}%)
        </span>
      </div>
      {cache.providerFamily && (
        <div className="aiClerk-ctxind__cache-provider">
          上游归属: {cache.providerFamily}
        </div>
      )}
    </div>
  )
}

/* ── Tool invocation row ─────────────────────────────── */

const ToolInvocationRow: React.FC<{ inv: ToolInvocationEntry }> = ({ inv }) => (
  <div className="aiClerk-ctxind__inv-row">
    <div className="aiClerk-ctxind__inv-head">
      <span className="aiClerk-ctxind__inv-name">
        {inv.sequenceIndex !== undefined && (
          <span className="aiClerk-ctxind__inv-seq">#{inv.sequenceIndex}</span>
        )}
        {inv.toolName}
      </span>
      <span className="aiClerk-ctxind__inv-tokens">
        {formatTokens(inv.tokens)}
        {inv.percentOfContext !== undefined && (
          <span className="aiClerk-ctxind__inv-pct">
            ({formatPercent(inv.percentOfContext)})
          </span>
        )}
      </span>
    </div>
    {(inv.textTokens !== undefined || inv.imageTokens) && (
      <div className="aiClerk-ctxind__inv-meta">
        {inv.textTokens !== undefined && `text ${formatTokens(inv.textTokens)}`}
        {inv.imageTokens ? ` · image ${formatTokens(inv.imageTokens)}` : ''}
      </div>
    )}
    {inv.preview && (
      <div className="aiClerk-ctxind__inv-preview">{inv.preview}</div>
    )}
  </div>
)

/* ── Message entry row ───────────────────────────────── */

const MessageEntryRow: React.FC<{ entry: MessageEntry }> = ({ entry }) => (
  <div className="aiClerk-ctxind__msg-row">
    <span className="aiClerk-ctxind__msg-text">
      <span className="aiClerk-ctxind__msg-role">{entry.role}</span>
      {entry.toolName ? (
        <span className="aiClerk-ctxind__msg-tool">
          [tool_result · {entry.toolName}]
        </span>
      ) : (
        <span className="aiClerk-ctxind__msg-preview">
          {entry.preview || '(空消息)'}
        </span>
      )}
      {entry.hasImage && (
        <span className="aiClerk-ctxind__msg-image-flag">🖼</span>
      )}
    </span>
    <span className="aiClerk-ctxind__msg-tokens">
      {formatTokens(entry.tokens)}
      {entry.percentOfContext !== undefined && (
        <span className="aiClerk-ctxind__msg-pct">
          ({formatPercent(entry.percentOfContext)})
        </span>
      )}
    </span>
  </div>
)

/* ── Multimodal ──────────────────────────────────────── */

const MultimodalPanel: React.FC<{ mm: MultimodalBreakdown }> = ({ mm }) => (
  <div className="aiClerk-ctxind__mm">
    <div className="aiClerk-ctxind__mm-head">
      <span className="aiClerk-ctxind__mm-title">
        多模态 ({mm.imageCount} 张图)
      </span>
      <span className="aiClerk-ctxind__mm-total">
        {formatTokens(mm.totalTokens)}
      </span>
    </div>
    {mm.images.slice(0, 5).map((img) => (
      <div
        key={`${img.messageId ?? 'noid'}-${img.width}x${img.height}-${
          img.tokens
        }`}
        className="aiClerk-ctxind__mm-row"
      >
        <span className="aiClerk-ctxind__mm-size">
          {img.width}×{img.height}
          {img.formula && (
            <span className="aiClerk-ctxind__mm-formula">({img.formula})</span>
          )}
        </span>
        <span className="aiClerk-ctxind__mm-tokens">
          {formatTokens(img.tokens)}
        </span>
      </div>
    ))}
    {mm.images.length > 5 && (
      <div className="aiClerk-ctxind__mm-rest">
        ... 还有 {mm.images.length - 5} 张
      </div>
    )}
  </div>
)

/* ── Tool schema row ─────────────────────────────────── */

const ToolSchemaRow: React.FC<{ entry: ToolSchemaEntry }> = ({ entry }) => (
  <div className="aiClerk-ctxind__schema-row">
    <span className="aiClerk-ctxind__schema-name">{entry.name}</span>
    <span className="aiClerk-ctxind__schema-tokens">
      {formatTokens(entry.tokens)}
    </span>
  </div>
)

/* ── Popover detail body ─────────────────────────────── */

const ContextUsageDetail: React.FC<{ usage: SessionContextUsage }> = ({
  usage
}) => {
  const tone = getContextUsageTone(usage)
  const estimated = estimateUsedTokens(usage)
  const ctx = usage.contextTokens
  const hasPercent = usage.usedPercent !== undefined
  const displayPercent = hasPercent
    ? usage.usedPercent
    : estimated !== undefined && ctx && ctx > 0
      ? (estimated / ctx) * 100
      : undefined
  const barWidth =
    displayPercent === undefined
      ? 0
      : Math.max(2, Math.min(100, displayPercent))

  const remainingPart: ContextUsagePart | null =
    usage.remainingTokens !== undefined && ctx
      ? {
          id: 'remaining',
          label: '剩余空间',
          tokens: usage.remainingTokens,
          percentOfContext:
            ctx > 0
              ? Math.round((usage.remainingTokens / ctx) * 100)
              : undefined
        }
      : null

  const hasParts = usage.parts.length > 0

  const usedTokensStr =
    usage.totalTokens !== undefined
      ? formatTokens(usage.totalTokens)
      : estimated !== undefined
        ? formatTokens(estimated)
        : ''
  const ctxTokensStr = ctx !== undefined ? formatTokens(ctx) : ''
  const [usedNum, usedUnit] = splitNumberUnit(usedTokensStr)
  const [ctxNum, ctxUnit] = splitNumberUnit(ctxTokensStr)
  const isEstimate = usage.totalTokens === undefined && estimated !== undefined

  return (
    <div className="aiClerk-ctxind__detail">
      {/* Warnings 横幅 */}
      {usage.warnings && usage.warnings.length > 0 && (
        <div className="aiClerk-ctxind__warnings">
          {usage.warnings.map((w) => (
            <div
              key={w}
              className={`aiClerk-ctxind__warning${
                w.includes('95%') ? ' is-critical' : ' is-warn'
              }`}
            >
              <span aria-hidden>⚠</span>
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Hero header */}
      <div className="aiClerk-ctxind__hero">
        <div className="aiClerk-ctxind__hero-head">
          <span className="aiClerk-ctxind__hero-label">上下文用量</span>
          {displayPercent !== undefined && (
            <span className="aiClerk-ctxind__hero-pct">
              {!hasPercent && (
                <span className="aiClerk-ctxind__hero-approx">≈</span>
              )}
              <span className="aiClerk-ctxind__hero-pct-num">
                {Math.round(displayPercent)}
              </span>
              <span className="aiClerk-ctxind__hero-pct-unit">%</span>
            </span>
          )}
        </div>

        {displayPercent !== undefined && (
          <div className="aiClerk-ctxind__bar">
            <div
              className={`aiClerk-ctxind__bar-fill ${TONE_CLASS[tone]}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        )}

        {usedTokensStr && ctxTokensStr && (
          <div className="aiClerk-ctxind__tokens">
            {isEstimate && (
              <span className="aiClerk-ctxind__tokens-approx">≈</span>
            )}
            <span className="aiClerk-ctxind__tokens-used-num">{usedNum}</span>
            {usedUnit && (
              <span className="aiClerk-ctxind__tokens-used-unit">
                {usedUnit}
              </span>
            )}
            <span className="aiClerk-ctxind__tokens-slash">/</span>
            <span className="aiClerk-ctxind__tokens-ctx-num">{ctxNum}</span>
            {ctxUnit && (
              <span className="aiClerk-ctxind__tokens-ctx-unit">{ctxUnit}</span>
            )}
            <span className="aiClerk-ctxind__tokens-suffix">
              tokens{isEstimate && ' (估算)'}
            </span>
          </div>
        )}

        {usage.effectiveLimit !== undefined &&
          usage.maxOutputTokens !== undefined && (
            <div className="aiClerk-ctxind__effective">
              <span className="aiClerk-ctxind__effective-dot" />
              <span className="aiClerk-ctxind__effective-text">
                有效 input 空间{' '}
                <span className="aiClerk-ctxind__effective-value">
                  {formatTokens(usage.effectiveLimit)}
                </span>
                <span className="aiClerk-ctxind__effective-meta">
                  {' '}
                  (扣除 max_output_tokens={' '}
                  <span className="aiClerk-ctxind__effective-mo">
                    {formatTokens(usage.maxOutputTokens)}
                  </span>
                  )
                </span>
              </span>
            </div>
          )}
      </div>

      <div className="aiClerk-ctxind__divider" />

      {/* 占用明细 */}
      {hasParts && (
        <div className="aiClerk-ctxind__section">
          <div className="aiClerk-ctxind__section-title">占用明细</div>
          <div className="aiClerk-ctxind__part-list">
            {usage.parts.map((p) => (
              <PartRow key={p.id} part={p} />
            ))}
            {remainingPart && <PartRow part={remainingPart} filled={false} />}
          </div>
        </div>
      )}

      {/* debugParts: 有 parts 时折叠为「详细分项」, 没有就当主明细显示 */}
      {usage.debugParts.length > 0 &&
        (hasParts ? (
          <CollapsibleSection title="详细分项">
            {usage.debugParts.map((p) => (
              <PartRow key={p.id} part={p} />
            ))}
          </CollapsibleSection>
        ) : (
          <div className="aiClerk-ctxind__section">
            <div className="aiClerk-ctxind__section-title">占用明细</div>
            <div className="aiClerk-ctxind__part-list">
              {usage.debugParts.map((p) => (
                <PartRow key={p.id} part={p} />
              ))}
              {remainingPart && <PartRow part={remainingPart} filled={false} />}
            </div>
          </div>
        ))}

      {/* Cache 三栏 */}
      {usage.cacheBreakdown && (
        <CacheBreakdownPanel cache={usage.cacheBreakdown} />
      )}

      {/* 系统提示词分段 */}
      {usage.systemPromptSections && usage.systemPromptSections.length > 0 && (
        <CollapsibleSection
          title="系统提示词分段"
          count={usage.systemPromptSections.length}
        >
          {usage.systemPromptSections.map((p) => (
            <PartRow key={p.id} part={p} />
          ))}
        </CollapsibleSection>
      )}

      {/* 工具调用明细 */}
      {usage.toolInvocations && usage.toolInvocations.length > 0 && (
        <CollapsibleSection
          title="工具调用明细"
          count={usage.toolInvocations.length}
        >
          {usage.toolInvocations.map((inv) => (
            <ToolInvocationRow
              key={`${inv.toolCallId}-${inv.sequenceIndex ?? 0}`}
              inv={inv}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* 消息明细 */}
      {usage.messageEntries && usage.messageEntries.length > 0 && (
        <CollapsibleSection
          title="消息明细 (前 N)"
          count={usage.messageEntries.length}
        >
          {usage.messageEntries.map((entry, index) => (
            <MessageEntryRow
              key={entry.messageId || `msg-${index}`}
              entry={entry}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* 多模态 */}
      {usage.multimodal && usage.multimodal.imageCount > 0 && (
        <MultimodalPanel mm={usage.multimodal} />
      )}

      {/* Tool schema */}
      {usage.toolSchemaEntries && usage.toolSchemaEntries.length > 0 && (
        <CollapsibleSection
          title="工具 Schema 明细"
          count={usage.toolSchemaEntries.length}
        >
          {usage.toolSchemaEntries.map((entry) => (
            <ToolSchemaRow key={entry.name} entry={entry} />
          ))}
        </CollapsibleSection>
      )}

      {/* Tool entries (旧字段) */}
      {usage.toolEntries && usage.toolEntries.length > 0 && (
        <CollapsibleSection title="工具明细" count={usage.toolEntries.length}>
          {usage.toolEntries.map((e) => (
            <EntryRow key={e.name} entry={e} />
          ))}
        </CollapsibleSection>
      )}

      {/* Skill entries */}
      {usage.skillEntries && usage.skillEntries.length > 0 && (
        <CollapsibleSection title="技能明细" count={usage.skillEntries.length}>
          {usage.skillEntries.map((e) => (
            <EntryRow key={e.name} entry={e} />
          ))}
        </CollapsibleSection>
      )}

      {/* File entries */}
      {usage.fileEntries && usage.fileEntries.length > 0 && (
        <CollapsibleSection title="注入文件" count={usage.fileEntries.length}>
          {usage.fileEntries.map((e) => (
            <EntryRow key={e.name} entry={e} />
          ))}
        </CollapsibleSection>
      )}
    </div>
  )
}

/* ── 环形 trigger ────────────────────────────────────── */

const RADIUS = 8
const CIRC = 2 * Math.PI * RADIUS

export const ContextUsageIndicator: React.FC<Props> = ({ usage }) => {
  if (!usage) {
    return (
      <div
        className="aiClerk-ctxind aiClerk-ctxind--empty"
        title="无上下文用量数据"
      >
        <svg viewBox="0 0 20 20" className="aiClerk-ctxind__ring" aria-hidden>
          <circle
            cx="10"
            cy="10"
            r={RADIUS}
            fill="none"
            strokeWidth="2.5"
            className="aiClerk-ctxind__ring-track"
          />
        </svg>
      </div>
    )
  }
  const tone = getContextUsageTone(usage)
  const percent = usage.usedPercent
  const hasPercent = percent !== undefined
  const clamped =
    percent === undefined ? 0 : Math.max(0, Math.min(100, percent))
  const freshPrefix = usage.totalTokensFresh === false ? '~' : ''
  const dashOffset = CIRC * (1 - clamped / 100)
  const tooltip = hasPercent
    ? `${freshPrefix}${formatPercent(percent)} · ${formatTokens(
        usage.totalTokens
      )} / ${formatTokens(usage.contextTokens)}`
    : `上下文 ${formatTokens(usage.contextTokens)}`

  return (
    <Popover
      content={<ContextUsageDetail usage={usage} />}
      trigger="click"
      placement="topLeft"
      classNames={{ root: 'aiClerk-ctxind__popover' }}
    >
      <button
        type="button"
        className={`aiClerk-ctxind ${TONE_CLASS[tone]}`}
        title={tooltip}
        aria-label={tooltip}
      >
        <svg viewBox="0 0 20 20" className="aiClerk-ctxind__ring" aria-hidden>
          <circle
            cx="10"
            cy="10"
            r={RADIUS}
            fill="none"
            strokeWidth="2.5"
            className="aiClerk-ctxind__ring-track"
          />
          {hasPercent && (
            <circle
              cx="10"
              cy="10"
              r={RADIUS}
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              className={`aiClerk-ctxind__ring-fill ${TONE_CLASS[tone]}`}
            />
          )}
        </svg>
      </button>
    </Popover>
  )
}

export default ContextUsageIndicator
